/**
 * CONTENIDO de la ayuda del PORTAL PARTNER.
 *
 * Mismo patrón —y a propósito— que `pages/customer/help/helpContent.ts`: el
 * texto vive aquí, bilingüe y en bloques tipados, en vez de en los ficheros de
 * i18n. El motivo es que esto no son rótulos de interfaz sino PROSA larga con
 * estructura (párrafos, listas, pasos, avisos): meterla en un JSON plano de
 * claves la vuelve imposible de leer y de mantener en dos idiomas a la vez, y
 * un desajuste entre es/en pasaría desapercibido. Aquí las dos versiones están
 * literalmente una al lado de la otra.
 *
 * Cubre lo que un partner NO puede deducir mirando la pantalla, que es casi
 * todo lo importante de este negocio:
 *   · qué es su bolsa y qué es su LÍMITE DE COMPRA MENSUAL (compra a granel
 *     libre hasta el límite del mes; subir ese límite es lo único que se pide)
 *     — la confusión número uno;
 *   · las dos formas de dar créditos a un cliente (plan mensual que caduca y
 *     recarga puntual que no) y cuál le sale a cuenta;
 *   · dónde está su margen y cómo se calcula;
 *   · por qué el almacenamiento de toda su cartera lo paga él;
 *   · qué es un crédito y qué es un importe — y por qué nunca son lo mismo.
 */

export type Locale = 'es' | 'en';

export const resolveLocale = (lang?: string): Locale =>
  (lang || '').toLowerCase().startsWith('es') ? 'es' : 'en';

/** Par «rótulo → explicación» de las listas y los pasos. */
export interface Item {
  k: string;
  v: string;
}

export type Block =
  | { kind: 'p'; es: string; en: string }
  | { kind: 'h'; es: string; en: string }
  | { kind: 'tip'; es: string; en: string }
  | { kind: 'warn'; es: string; en: string }
  | { kind: 'list'; es: Item[]; en: Item[] }
  | { kind: 'steps'; es: Item[]; en: Item[] };

export type PartnerSectionId =
  | 'programa'
  | 'bolsa'
  | 'clientes'
  | 'creditos'
  | 'catalogo'
  | 'almacenamiento'
  | 'facturacion'
  | 'faq';

/** Icono de la sección. Nombre abstracto: el contenido no importa MUI, la
 *  página traduce estas claves a componentes. */
export type PartnerSectionIcon =
  | 'start'
  | 'wallet'
  | 'clients'
  | 'credits'
  | 'catalog'
  | 'storage'
  | 'billing'
  | 'faq';

export interface PartnerSection {
  id: PartnerSectionId;
  icon: PartnerSectionIcon;
  es: { title: string; lead: string };
  en: { title: string; lead: string };
  blocks: Block[];
}

export const PARTNER_HELP_SECTIONS: PartnerSection[] = [
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'programa',
    icon: 'start',
    es: {
      title: 'Empieza por aquí',
      lead: 'En cinco minutos entiendes el negocio entero: qué compras, qué vendes, dónde está tu margen y qué decisiones son tuyas y cuáles del marketplace.',
    },
    en: {
      title: 'Start here',
      lead: 'Five minutes and you understand the whole business: what you buy, what you sell, where your margin is, and which decisions are yours and which belong to the marketplace.',
    },
    blocks: [
      {
        kind: 'p',
        es: 'Como partner compras CRÉDITOS al por mayor, con tu descuento, y los repartes entre los clientes de tu cartera. Tus clientes usan esos créditos para que trabajen sus empleados IA. Tú les facturas a ellos al precio que decidas; el marketplace te factura a ti solo lo que compras.',
        en: 'As a partner you buy CREDITS wholesale, with your discount, and distribute them among the clients in your portfolio. Your clients spend those credits running their AI employees. You invoice them at whatever price you choose; the marketplace only invoices you for what you buy.',
      },
      {
        kind: 'h',
        es: 'El recorrido completo, en cuatro pasos',
        en: 'The whole journey, in four steps',
      },
      {
        kind: 'steps',
        es: [
          {
            k: 'Compra créditos',
            v: 'Desde «Comprar créditos» o «Facturación». No hay que pedir permiso: compras a granel cuando quieras mientras quepa en tu límite de compra de este mes.',
          },
          {
            k: 'Da de alta a tu cliente',
            v: 'En «Clientes». Se crea su cuenta y su acceso al portal de cliente; a partir de ahí es él quien contrata y gobierna sus empleados.',
          },
          {
            k: 'Dale créditos',
            v: 'Desde la ficha del cliente, en plan mensual (recurrente, caduca) o recarga puntual (no caduca). Salen de tu bolsa.',
          },
          {
            k: 'Cobra tu margen',
            v: 'Tú le facturas a tu cliente lo que hayáis pactado. El marketplace te pasa a fin de mes solo tus compras, con tu descuento aplicado.',
          },
        ],
        en: [
          {
            k: 'Buy credits',
            v: 'From “Buy credits” or “Billing”. No permission needed: buy in bulk whenever you like, as long as it fits under this month’s purchase limit.',
          },
          {
            k: 'Register your client',
            v: 'In “Clients”. Their account and their client-portal access are created; from then on they hire and govern their own employees.',
          },
          {
            k: 'Give them credits',
            v: 'From the client page, as a monthly plan (recurring, expires) or a one-off top-up (never expires). Both come out of your balance.',
          },
          {
            k: 'Collect your margin',
            v: 'You invoice your client whatever you agreed. At month end the marketplace only bills you for your purchases, with your discount applied.',
          },
        ],
      },
      {
        kind: 'tip',
        es: 'Tu cliente nunca ve el marketplace ni recibe ninguna factura nuestra: en su portal aparece tu nombre y tu email como quien le gestiona la cuenta.',
        en: 'Your client never sees the marketplace and never receives an invoice from us: their portal shows your name and your email as the company managing their account.',
      },
    ],
  },
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'bolsa',
    icon: 'wallet',
    es: {
      title: 'Tu bolsa y tu límite de compra mensual',
      lead: 'Dos cosas distintas que se confunden a todas horas: la BOLSA es lo que tienes; el LÍMITE DE COMPRA MENSUAL es cuántos créditos puedes comprar cada mes.',
    },
    en: {
      title: 'Your balance and your monthly purchase limit',
      lead: 'Two different things that get confused constantly: your BALANCE is what you have; your MONTHLY PURCHASE LIMIT is how many credits you can buy each month.',
    },
    blocks: [
      {
        kind: 'list',
        es: [
          {
            k: 'La bolsa mayorista',
            v: 'Los créditos que tienes ahora mismo sin repartir. Sube cuando compras y baja cuando asignas créditos a un cliente o cuando se te cobra el almacenamiento de tu cartera.',
          },
          {
            k: 'El límite de compra mensual',
            v: 'Cuántos créditos puedes COMPRAR por mes (no un tope de tu bolsa). Se mide sobre lo que compras, no sobre tu saldo: asignar créditos a un cliente baja tu bolsa pero NO te libera cupo de compra. Se renueva cada mes desde tu primera compra. Si aparece como «sin límite», compras sin restricción.',
          },
        ],
        en: [
          {
            k: 'The wholesale balance',
            v: 'The credits you currently hold and have not distributed. It goes up when you buy and down when you assign credits to a client or when your portfolio storage is charged.',
          },
          {
            k: 'The monthly purchase limit',
            v: 'How many credits you can BUY per month (not a cap on your balance). It is measured against what you buy, not your balance: assigning credits to a client lowers your balance but does NOT free up purchase room. It renews every month from your first purchase. If it shows as “no limit”, you buy without restriction.',
          },
        ],
      },
      {
        kind: 'p',
        es: 'Comprar es self-service: no hay ninguna aprobación por compra. Compras a granel a tu precio mayorista plano (el mismo por crédito compres 100 o 100.000). Solo se te frena si con esa compra superarías tu límite de compra de ESTE MES, y entonces el aviso te dice cuánto llevas comprado, cuánto te cabe todavía y cuándo se renueva.',
        en: 'Buying is self-service: there is no approval per purchase. You buy in bulk at your flat wholesale price (the same per credit whether you buy 100 or 100,000). You are only stopped if a purchase would exceed THIS MONTH’s purchase limit, and the message tells you how much you have bought, how much still fits and when it renews.',
      },
      {
        kind: 'h',
        es: 'Lo único que se solicita: subir el límite mensual',
        en: 'The only thing you request: raising the monthly limit',
      },
      {
        kind: 'p',
        es: 'Cuando el límite se te queda corto, pides una ampliación desde «Comprar créditos» o «Facturación». No estás pidiendo créditos —esos los compras tú—: pides que te suban el límite de compra mensual. Al aprobarla no te aparece saldo nuevo; aparece más margen para comprar este mes.',
        en: 'When the limit becomes too tight you request an increase from “Buy credits” or “Billing”. You are not requesting credits —you buy those yourself—: you are requesting a higher monthly purchase limit. Approval does not add balance; it adds room to buy this month.',
      },
      {
        kind: 'warn',
        es: 'Bajar tu bolsa a cero no rompe nada tuyo, pero sí frena a tus clientes: sin créditos asignados, sus empleados dejan de trabajar hasta la siguiente asignación.',
        en: 'Running your balance down to zero breaks nothing on your side, but it does stop your clients: with no credits assigned, their employees stop working until the next assignment.',
      },
    ],
  },
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'clientes',
    icon: 'clients',
    es: {
      title: 'Tu cartera de clientes',
      lead: 'Das de alta al cliente, le pones sus límites y a partir de ahí trabaja solo. Tú decides cuánta cuerda le das.',
    },
    en: {
      title: 'Your client portfolio',
      lead: 'You register the client, set their limits, and from then on they work on their own. You decide how much rope to give them.',
    },
    blocks: [
      {
        kind: 'p',
        es: 'Al dar de alta a un cliente se crea su cuenta y el acceso de su propietario al portal de cliente. Desde ese momento es él quien contrata empleados, añade usuarios y los pone a trabajar; tú te quedas con las decisiones comerciales: cuántos empleados puede tener, cuánto disco y cuántos créditos.',
        en: 'Registering a client creates their account and their owner’s access to the client portal. From then on they hire employees, add users and put them to work; you keep the commercial decisions: how many employees they may have, how much storage and how many credits.',
      },
      {
        kind: 'list',
        es: [
          {
            k: 'Cupo de empleados',
            v: 'Cuántos empleados IA puede tener a la vez esa cuenta. Si está lleno, el cliente no puede contratar más hasta que se lo amplíes o retire uno.',
          },
          {
            k: 'Almacenamiento',
            v: 'Cuánto disco le incluyes sin coste y cuál es su techo. Sale de TU capacidad y lo pagas tú, así que es una decisión comercial tuya.',
          },
          {
            k: 'Ver como cliente',
            v: 'Abre su portal en modo solo lectura durante unos minutos. Sirve para acompañarle por teléfono viendo exactamente lo mismo que él; no puedes tocar nada.',
          },
        ],
        en: [
          {
            k: 'Employee quota',
            v: 'How many AI employees that account may have at once. When it is full the client cannot hire more until you raise it or they remove one.',
          },
          {
            k: 'Storage',
            v: 'How much disk you include at no cost and what their ceiling is. It comes out of YOUR capacity and you pay for it, so it is your commercial decision.',
          },
          {
            k: 'View as client',
            v: 'Opens their portal read-only for a few minutes. Useful to walk them through something on the phone seeing exactly what they see; you cannot change anything.',
          },
        ],
      },
      {
        kind: 'warn',
        es: 'Retirar un empleado de un cliente borra su aplicación y sus datos y libera su plaza del cupo. No es una pausa: volver a tenerlo significa contratarlo de nuevo, desde cero.',
        en: 'Removing an employee from a client deletes its application and its data and frees the quota slot. It is not a pause: getting it back means hiring it again from scratch.',
      },
    ],
  },
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'creditos',
    icon: 'credits',
    es: {
      title: 'Dar créditos a un cliente',
      lead: 'Dos formas, y no son dos maneras de decir lo mismo: el plan mensual se renueva y caduca; la recarga es puntual y no caduca.',
    },
    en: {
      title: 'Giving credits to a client',
      lead: 'Two ways, and they are not two names for the same thing: the monthly plan renews and expires; the top-up is one-off and never expires.',
    },
    blocks: [
      {
        kind: 'list',
        es: [
          {
            k: 'Plan mensual (recurrente)',
            v: 'Cada ciclo el cliente recibe N créditos y lo que no gasta caduca al cerrarlo. OJO: cada renovación vuelve a salir de tu bolsa mayorista, todos los meses, hasta que retires el plan.',
          },
          {
            k: 'Recarga puntual',
            v: 'Un traspaso único que no caduca: se queda en el saldo del cliente hasta que lo use. Ideal para un pico de trabajo o para cerrar un caso concreto.',
          },
        ],
        en: [
          {
            k: 'Monthly plan (recurring)',
            v: 'Every cycle the client receives N credits and whatever they do not spend expires when it closes. Note: every renewal comes out of your wholesale balance again, every month, until you remove the plan.',
          },
          {
            k: 'One-off top-up',
            v: 'A single transfer that never expires: it stays in the client’s balance until used. Ideal for a busy stretch or to close a specific case.',
          },
        ],
      },
      {
        kind: 'p',
        es: 'El cliente gasta primero los créditos del plan (los que iban a caducar) y después los extra. Así aprovecha antes lo que iba a perder, sin que nadie tenga que acordarse del orden.',
        en: 'The client spends plan credits first (the ones that were going to expire) and extra credits afterwards. That way they use up what they were going to lose, with nobody having to remember the order.',
      },
      {
        kind: 'tip',
        es: 'Poner plan a un cliente estable es lo que convierte tu cartera en ingreso recurrente. Guarda las recargas para lo excepcional: si un cliente vive de recargas, el plan le habría salido más barato y a ti te habría dado previsión.',
        en: 'Putting a stable client on a plan is what turns your portfolio into recurring revenue. Keep top-ups for the exceptional: if a client lives on top-ups, a plan would have been cheaper for them and more predictable for you.',
      },
      {
        kind: 'warn',
        es: 'Antes de activar un plan mira tu bolsa: se te exige saldo para el primer ciclo, y los siguientes se cobrarán solos cada mes. Un plan olvidado sigue drenando tu bolsa aunque el cliente no lo use.',
        en: 'Check your balance before activating a plan: you must have enough for the first cycle, and the following ones are charged automatically every month. A forgotten plan keeps draining your balance even if the client does not use it.',
      },
    ],
  },
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'catalogo',
    icon: 'catalog',
    es: {
      title: 'El catálogo y tu margen',
      lead: 'El mismo catálogo que ve tu cliente, con tus números encima: lo que puedes cobrarle, lo que te cuesta y la diferencia.',
    },
    en: {
      title: 'The catalog and your margin',
      lead: 'The same catalog your client sees, with your numbers on top: what you can charge, what it costs you, and the difference.',
    },
    blocks: [
      {
        kind: 'list',
        es: [
          {
            k: 'Precio de referencia',
            v: 'Lo que el marketplace sugiere cobrar por ese paquete. Es una referencia, no una obligación: tú facturas a tu cliente al precio que quieras.',
          },
          {
            k: 'Tu coste',
            v: 'Lo que te factura el marketplace por esos créditos: créditos × precio de lista × (1 − tu descuento). Es exactamente lo que aparecerá en tu factura del mes.',
          },
          {
            k: 'Tu margen',
            v: 'La diferencia entre ambos, en dinero y en porcentaje. Es tu negocio en una cifra.',
          },
        ],
        en: [
          {
            k: 'Reference price',
            v: 'What the marketplace suggests charging for that package. A reference, not an obligation: you invoice your client at whatever price you want.',
          },
          {
            k: 'Your cost',
            v: 'What the marketplace bills you for those credits: credits × list price × (1 − your discount). Exactly what will appear on your monthly invoice.',
          },
          {
            k: 'Your margin',
            v: 'The difference between the two, in money and as a percentage. Your business in a single figure.',
          },
        ],
      },
      {
        kind: 'p',
        es: 'Los paquetes grandes salen más baratos por crédito para tu cliente, pero tu coste por crédito es siempre el mismo (tu descuento no cambia con el tamaño). Es decir: el descuento por volumen sale de tu margen, y por eso lo decides tú al fijar tu precio.',
        en: 'Bigger packages are cheaper per credit for your client, but your cost per credit is always the same (your discount does not change with size). In other words: the volume discount comes out of your margin, which is why you decide it when you set your price.',
      },
      {
        kind: 'tip',
        es: 'La columna «Para qué vale» está escrita en lenguaje de negocio a propósito: úsala tal cual delante del cliente. Explica un paquete mucho mejor que un número de créditos.',
        en: 'The “What it covers” column is deliberately written in business language: use it as is in front of a client. It explains a package far better than a credit count.',
      },
    ],
  },
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'almacenamiento',
    icon: 'storage',
    es: {
      title: 'El almacenamiento de tu cartera',
      lead: 'Cada empleado ocupa disco real. El de TODOS tus clientes se te cobra a ti, una sola vez, y tú lo repartes.',
    },
    en: {
      title: 'Your portfolio storage',
      lead: 'Every employee takes real disk space. The disk of ALL your clients is billed to you, once, and you distribute it.',
    },
    blocks: [
      {
        kind: 'p',
        es: 'La regla es que el almacenamiento se cobra UNA sola vez y a quien tiene la relación comercial. Tus clientes no pagan disco: lo pagas tú, por el total de tu cartera que exceda lo que el marketplace te incluye, con un cargo por ciclo en tu bolsa.',
        en: 'The rule is that storage is charged ONCE and to whoever holds the commercial relationship. Your clients do not pay for disk: you do, for whatever your whole portfolio exceeds the amount the marketplace includes for you, as a per-cycle charge against your balance.',
      },
      {
        kind: 'list',
        es: [
          {
            k: 'Incluido',
            v: 'Los GiB que el marketplace te regala sobre el total de tu cartera. Solo lo que pase de ahí genera cargo.',
          },
          {
            k: 'Techo agregado',
            v: 'El máximo de disco que puede llegar a ocupar toda tu cartera. Gobierna cuánto puedes prometer.',
          },
          {
            k: 'Lo que repartes',
            v: 'De tu capacidad das a cada cliente su «incluido» y su techo. Es tu decisión comercial: puedes ser generoso con uno y estricto con otro.',
          },
        ],
        en: [
          {
            k: 'Included',
            v: 'The GiB the marketplace gives you for free across your whole portfolio. Only what goes beyond generates a charge.',
          },
          {
            k: 'Aggregate ceiling',
            v: 'The maximum disk your entire portfolio may occupy. It governs how much you can promise.',
          },
          {
            k: 'What you distribute',
            v: 'From your capacity you give each client their “included” amount and their ceiling. It is your commercial call: generous with one, strict with another.',
          },
        ],
      },
      {
        kind: 'warn',
        es: 'Ampliar el disco de un empleado es permanente: un volumen no se encoge sin recrearlo. Mientras ese empleado exista, sus GiB cuentan para tu cargo por ciclo. Solo retirarlo los libera.',
        en: 'Expanding an employee’s disk is permanent: a volume cannot shrink without being recreated. As long as that employee exists, its GiB count towards your per-cycle charge. Only removing it frees them.',
      },
    ],
  },
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'facturacion',
    icon: 'billing',
    es: {
      title: 'Facturación y movimientos',
      lead: 'Tres vistas del mismo dinero: el resumen del mes, tus compras con su importe, y el extracto movimiento a movimiento.',
    },
    en: {
      title: 'Billing and movements',
      lead: 'Three views of the same money: the monthly summary, your purchases with their amounts, and the movement-by-movement statement.',
    },
    blocks: [
      {
        kind: 'list',
        es: [
          {
            k: 'Resumen del mes',
            v: 'Agregados del periodo y el total a facturar. Contesta a «cuánto», no a «cuál». Solo se factura lo que COMPRAS: lo que asignas a clientes ya estaba comprado.',
          },
          {
            k: 'Tus compras',
            v: 'Cada compra con su fecha, los créditos que entraron y el importe en USD que se te factura por ella, con tu descuento ya aplicado. Es la tabla para cuadrar la factura.',
          },
          {
            k: 'Movimientos de tu bolsa',
            v: 'El extracto completo: compras, asignaciones a clientes, ajustes y cargos de disco. Es lo que contesta a «¿por qué me bajó el saldo el martes?».',
          },
        ],
        en: [
          {
            k: 'Monthly summary',
            v: 'Period aggregates and the total to invoice. It answers “how much”, not “which”. Only PURCHASES are invoiced: what you assign to clients was already bought.',
          },
          {
            k: 'Your purchases',
            v: 'Every purchase with its date, the credits that came in and the USD amount billed for it, with your discount already applied. This is the table to reconcile your invoice.',
          },
          {
            k: 'Your balance movements',
            v: 'The full statement: purchases, client assignments, adjustments and disk charges. This is what answers “why did my balance drop on Tuesday?”.',
          },
        ],
      },
      {
        kind: 'h',
        es: 'Créditos e importes no son lo mismo',
        en: 'Credits and amounts are not the same thing',
      },
      {
        kind: 'p',
        es: 'Un CRÉDITO es la unidad de trabajo de tu bolsa; un IMPORTE es dinero, en USD. En todo el portal, donde pone «créditos» hay créditos y donde pone «importe» hay dólares. Solo las compras tienen importe: una asignación a un cliente o un cargo de disco gastan créditos que ya compraste y facturaste, así que ponerles precio sería cobrarlos dos veces.',
        en: 'A CREDIT is the working unit of your balance; an AMOUNT is money, in USD. Throughout the portal, where it says “credits” there are credits, and where it says “amount” there are dollars. Only purchases carry an amount: assigning to a client or a disk charge spend credits you already bought and were billed for, so pricing them would be charging twice.',
      },
      {
        kind: 'tip',
        es: 'Sin pasarela de pago: comprar no te cobra en el acto. Todo lo comprado en el mes llega junto en la factura mensual.',
        en: 'There is no payment gateway: buying does not charge you on the spot. Everything bought during the month arrives together in the monthly invoice.',
      },
    ],
  },
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'faq',
    icon: 'faq',
    es: {
      title: 'Preguntas frecuentes',
      lead: 'Las dudas que más se repiten, contestadas en una línea.',
    },
    en: {
      title: 'Frequently asked questions',
      lead: 'The most repeated doubts, answered in one line.',
    },
    blocks: [
      {
        kind: 'list',
        es: [
          {
            k: '¿Tengo que pedir permiso para comprar créditos?',
            v: 'No. Compras a granel cuando quieras mientras quepa en tu límite de compra de este mes. Lo único que se solicita es subir ese límite mensual.',
          },
          {
            k: 'He pedido una ampliación de límite, ¿me llegarán créditos?',
            v: 'No. Te subirán el límite de compra mensual; los créditos los compras tú después.',
          },
          {
            k: '¿Puedo recuperar créditos que ya asigné a un cliente?',
            v: 'No desde el portal. Es un traspaso hecho; si hubo un error, habla con el marketplace para que lo corrija con un ajuste.',
          },
          {
            k: 'Mi cliente dice que sus empleados se han parado',
            v: 'Casi siempre es saldo a cero. Míralo en su ficha y asígnale créditos: al recargar siguen donde estaban, no se pierde nada.',
          },
          {
            k: '¿Por qué me han cobrado almacenamiento si mi cliente no ha hecho nada?',
            v: 'El disco se cuenta por el total de tu cartera y se cobra por ciclo, exista o no actividad. Ampliar el disco de un empleado es permanente.',
          },
          {
            k: '¿Mi cliente ve mis precios o mi descuento?',
            v: 'Nunca. Los números de coste y margen son tuyos y solo se ven en tu portal.',
          },
        ],
        en: [
          {
            k: 'Do I need permission to buy credits?',
            v: 'No. Buy in bulk whenever you like as long as it fits under this month’s purchase limit. The only thing you request is raising that monthly limit.',
          },
          {
            k: 'I requested a limit increase — will credits arrive?',
            v: 'No. Your monthly purchase limit will be raised; you buy the credits yourself afterwards.',
          },
          {
            k: 'Can I claw back credits I already assigned to a client?',
            v: 'Not from the portal. It is a completed transfer; if it was a mistake, ask the marketplace to correct it with an adjustment.',
          },
          {
            k: 'My client says their employees have stopped',
            v: 'Almost always a zero balance. Check their page and assign credits: after a top-up they resume where they were, nothing is lost.',
          },
          {
            k: 'Why was I charged for storage if my client did nothing?',
            v: 'Disk is counted across your whole portfolio and charged per cycle, activity or not. Expanding an employee’s disk is permanent.',
          },
          {
            k: 'Does my client see my prices or my discount?',
            v: 'Never. Cost and margin figures are yours and only appear in your portal.',
          },
        ],
      },
    ],
  },
];

// ── Ayuda POR PANTALLA ─────────────────────────────────────────────────
// Un bloque corto por pantalla del portal, para el panel «¿Cómo funciona esta
// pantalla?». Cada uno enlaza con la sección de la guía global que lo
// desarrolla: la ayuda contextual resuelve la duda del momento, la guía explica
// el modelo. Duplicar el texto largo aquí lo habría dejado desincronizado.

export type PartnerScreenId =
  | 'dashboard'
  | 'clients'
  | 'clientDetail'
  | 'packages'
  | 'billing';

export interface PartnerScreenHelp {
  id: PartnerScreenId;
  icon: PartnerSectionIcon;
  /** Sección de la guía global que desarrolla esta pantalla. */
  section: PartnerSectionId;
  es: { title: string; lead: string; points: string[] };
  en: { title: string; lead: string; points: string[] };
}

export const PARTNER_SCREEN_HELP: Record<PartnerScreenId, PartnerScreenHelp> = {
  dashboard: {
    id: 'dashboard',
    icon: 'start',
    section: 'programa',
    es: {
      title: 'Tu panel',
      lead: 'La foto de tu negocio: cuántos clientes tienes, cuánto te queda en la bolsa y cuánta capacidad has comprometido.',
      points: [
        'La «bolsa» son los créditos que tienes sin repartir; el «límite de compra mensual» es cuántos créditos puedes comprar cada mes.',
        'Los cupos (clientes, empleados, disco) son lo que has pactado con el marketplace: cuando uno se llena, deja de poder crecer por ahí.',
        'El cargo de almacenamiento de tu cartera se calcula al abrir esta pantalla: si toca, lo verás aquí antes que en la factura.',
        'Todo lo que se ve aquí es agregado de tu cartera; el detalle cliente a cliente está en «Clientes».',
      ],
    },
    en: {
      title: 'Your dashboard',
      lead: 'The snapshot of your business: how many clients you have, what is left in your balance and how much capacity you have committed.',
      points: [
        'The “balance” is the credits you hold undistributed; the “monthly purchase limit” is how many credits you can buy each month.',
        'Quotas (clients, employees, disk) are what you agreed with the marketplace: when one fills up, you cannot grow that way any more.',
        'Your portfolio storage charge is computed when you open this screen: if one is due, you see it here before it reaches the invoice.',
        'Everything here is aggregated across your portfolio; the client-by-client detail is under “Clients”.',
      ],
    },
  },
  clients: {
    id: 'clients',
    icon: 'clients',
    section: 'clientes',
    es: {
      title: 'Tus clientes',
      lead: 'Tu cartera completa, con el saldo y la ocupación de cada cuenta de un vistazo.',
      points: [
        'Dar de alta a un cliente crea su cuenta y el acceso de su propietario al portal de cliente: la contraseña se muestra UNA sola vez.',
        'El «cupo» de cada cliente es cuántos empleados IA puede tener a la vez; lo fijas tú y se puede ampliar después.',
        'El saldo que ves es el del CLIENTE (lo que le has asignado menos lo que ha consumido), no tu bolsa.',
        'Si un cliente está a cero, sus empleados están parados: entra en su ficha y asígnale créditos.',
      ],
    },
    en: {
      title: 'Your clients',
      lead: 'Your whole portfolio, with each account’s balance and usage at a glance.',
      points: [
        'Registering a client creates their account and their owner’s client-portal access: the password is shown ONCE.',
        'Each client’s “quota” is how many AI employees they may have at once; you set it and it can be raised later.',
        'The balance shown is the CLIENT’s (what you assigned minus what they consumed), not your own.',
        'If a client is at zero their employees are stopped: open their page and assign credits.',
      ],
    },
  },
  clientDetail: {
    id: 'clientDetail',
    icon: 'credits',
    section: 'creditos',
    es: {
      title: 'Ficha del cliente',
      lead: 'Todo lo que decides sobre un cliente: sus créditos, sus empleados y su espacio.',
      points: [
        'Al asignar créditos eliges entre PLAN mensual (recurrente: cada renovación vuelve a salir de tu bolsa y al cliente le caduca cada ciclo) y RECARGA puntual (una vez, no caduca).',
        'Asignar un empleado gasta cupo del cliente y cuenta para tus techos agregados de empleados y de disco.',
        'El espacio que le repartes sale de tu capacidad y lo pagas tú: por eso lo fijas tú y no él.',
        '«Ver como cliente» abre su portal en solo lectura unos minutos, para acompañarle sin poder tocar nada.',
        'Retirar un empleado borra su aplicación y sus datos y libera la plaza: es irreversible.',
      ],
    },
    en: {
      title: 'Client page',
      lead: 'Everything you decide about a client: their credits, their employees and their storage.',
      points: [
        'When assigning credits you choose between a monthly PLAN (recurring: every renewal comes out of your balance again and expires for the client each cycle) and a one-off TOP-UP (once, never expires).',
        'Assigning an employee consumes the client’s quota and counts towards your aggregate employee and disk ceilings.',
        'The space you hand them comes out of your capacity and you pay for it: that is why you set it, not them.',
        '“View as client” opens their portal read-only for a few minutes, to guide them without being able to change anything.',
        'Removing an employee deletes its application and data and frees the slot: it is irreversible.',
      ],
    },
  },
  packages: {
    id: 'packages',
    icon: 'catalog',
    section: 'catalogo',
    es: {
      title: 'El catálogo',
      lead: 'Lo mismo que ve tu cliente, con tus números: lo que puedes cobrarle, lo que te cuesta y lo que ganas.',
      points: [
        '«Precio de referencia» es una sugerencia de venta, no una obligación: tú facturas a tu cliente al precio que decidas.',
        '«Tu coste» es lo que te factura el marketplace por esos créditos, con tu descuento ya aplicado — la misma cifra que verás en tu factura.',
        'Los planes son recurrentes: cada renovación de un cliente vuelve a salir de tu bolsa, así que son ingreso recurrente y coste recurrente.',
        'Los packs extra no caducan y por eso salen algo más caros por crédito: es lo que hace que el plan le convenga a tu cliente.',
        'El texto «Para qué vale» está escrito para leerlo tal cual delante del cliente.',
      ],
    },
    en: {
      title: 'The catalog',
      lead: 'The same your client sees, with your numbers: what you can charge, what it costs you and what you make.',
      points: [
        '“Reference price” is a sales suggestion, not an obligation: you invoice your client at the price you choose.',
        '“Your cost” is what the marketplace bills you for those credits, discount already applied — the same figure you will see on your invoice.',
        'Plans are recurring: every client renewal comes out of your balance again, so they are recurring revenue and recurring cost.',
        'Extra packs never expire and therefore cost slightly more per credit: that is what makes the plan worthwhile for your client.',
        'The “What it covers” text is written to be read out loud to a client as is.',
      ],
    },
  },
  billing: {
    id: 'billing',
    icon: 'billing',
    section: 'facturacion',
    es: {
      title: 'Facturación',
      lead: 'Aquí compras créditos, sigues tus compras y cuadras lo que se te factura.',
      points: [
        'Comprar es self-service: no hay aprobación. Solo se te frena si con la compra superarías tu límite de compra de ESTE MES (se mide sobre lo comprado, no sobre tu saldo).',
        'Lo único que se solicita es SUBIR EL LÍMITE MENSUAL. Aprobarlo no te da créditos: te sube el cupo de compra del mes.',
        '«Tus compras» lleva la fecha, los créditos y el IMPORTE en USD de cada una: es la tabla para cuadrar la factura.',
        '«Movimientos de tu bolsa» es el extracto completo en créditos: compras, asignaciones a clientes, ajustes y cargos de disco.',
        'Solo se factura lo que compras. Lo que asignas a un cliente ya estaba comprado, y por eso no lleva importe.',
      ],
    },
    en: {
      title: 'Billing',
      lead: 'Here you buy credits, track your purchases and reconcile what you are billed.',
      points: [
        'Buying is self-service: no approval. You are only stopped if the purchase would exceed THIS MONTH’s purchase limit (measured against what you bought, not your balance).',
        'The only thing you request is RAISING THE MONTHLY LIMIT. Approval does not give you credits: it raises this month’s purchase room.',
        '“Your purchases” carries the date, the credits and the USD AMOUNT of each one: this is the table to reconcile your invoice.',
        '“Your balance movements” is the full statement in credits: purchases, client assignments, adjustments and disk charges.',
        'Only what you buy is invoiced. What you assign to a client was already bought, which is why it carries no amount.',
      ],
    },
  },
};
